import { FormEvent, useState } from 'react';
import type { HandOffConversation } from '../../types/handoff';
import { useSendAgentMessageMutation } from '../../hooks/useHandoff';

type Props = {
  conversation: HandOffConversation;
};

function canReply(conversation: HandOffConversation) {
  return conversation.handoffStatus === 'assigned' || conversation.handoffStatus === 'active';
}

export function AgentReplyComposer({ conversation }: Props) {
  const [content, setContent] = useState('');
  const sendMessage = useSendAgentMessageMutation();

  const disabled = !canReply(conversation) || sendMessage.isPending;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const trimmed = content.trim();

    if (!trimmed || disabled) return;

    await sendMessage.mutateAsync({
      conversationId: conversation.id,
      content: trimmed,
    });

    setContent('');
  }

  return (
    <form onSubmit={handleSubmit} className="border-t bg-white p-4">
      {!canReply(conversation) && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Ta över ärendet innan du svarar kunden.
        </div>
      )}

      <div className="flex items-end gap-3">
        <textarea
          value={content}
          onChange={(event) => setContent(event.target.value)}
          disabled={disabled}
          rows={3}
          placeholder="Skriv svar till kunden..."
          className="min-h-[84px] flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none ring-blue-500 transition placeholder:text-gray-400 focus:ring-2 disabled:bg-gray-50 disabled:text-gray-400"
        />

        <button
          type="submit"
          disabled={disabled || content.trim().length === 0}
          className="rounded-xl bg-gray-950 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          {sendMessage.isPending ? 'Skickar...' : 'Skicka'}
        </button>
      </div>
    </form>
  );
}
